import { isDevelopment, isLocal } from "@/config/prefs";
import { extractAuthentication, handleLogout, persistAuthentication } from "@/lib/auth";
import { APIResponseSuccessSchema } from "@/schema/api";
import { AuthenticationSchema } from "@/schema/authentication";
import { Mutex } from "async-mutex";

export interface ApiOptions extends RequestInit {
  multipart?: boolean;
}

export const mutex = new Mutex();
export const mSocketURL = isLocal ? "ws://localhost:8000" : isDevelopment ? "wss://qa-api.trybacon.ai/core" : "wss:/api.trybacon.ai/core";
export const mBaseURL = isLocal ? "http://localhost:8001" : isDevelopment ? "https://qa-api.trybacon.ai/core" : "https://api.trybacon.ai/core";
export const mBrandResearchBaseURL = isLocal ? "http://localhost:8000" : isDevelopment ? "https://qa-api.trybacon.ai/brand-research" : "https://api.trybacon.ai/brand-research";

export async function api(input: RequestInfo | URL, options?: ApiOptions, metadata?: any) {
  // Wait for any in-flight refresh BEFORE reading the access token from
  // localStorage. Otherwise a request that arrives during a refresh resumes
  // with the pre-refresh token and gets a guaranteed 401 — which then
  // kicks off a redundant refresh path. Long-running calls (e.g. the
  // insights agent run, ~10–30s) make this race trivially reproducible.
  await mutex.waitForUnlock();
  const accessToken = extractAuthentication().accessToken;
  const auth = "Bearer " + accessToken;

  let response = await fetch(input, {
    ...options,
    headers: {
      ...options?.headers,
      ...(options?.multipart ? {} : { "Content-Type": "application/json" }),
      ...(accessToken ? { Authorization: auth } : {}),
    },
  });

  // All HTTP services sit behind the same central middleware that validates
  // the core-issued JWT, so any 401 is — at least possibly — a stale
  // access token and worth one refresh attempt. The strict rule is:
  //
  //   - Refresh-endpoint failure  → session is genuinely dead → logout.
  //   - Retry-after-refresh 401   → token is fine; the middleware is
  //                                 rejecting for a non-token reason
  //                                 (missing role/membership, scope, etc.).
  //                                 Surface as a normal API error. Do
  //                                 NOT logout — that's overreach and
  //                                 silently kills the user's session
  //                                 over what should be a per-request
  //                                 error.
  if (response.status === 401) {
    const retry = metadata?.retry ?? false;

    if (retry) {
      // We already refreshed once on this request and the new token still
      // came back 401 from the same endpoint. That's not a token problem.
      // Pass the 401 back to the caller as-is.
      return response;
    }

    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        // Re-read tokens AFTER acquiring the mutex. If another concurrent
        // 401-handler already refreshed while we were queueing on the
        // mutex, the access token has changed — just retry with the new
        // one, don't burn a second refresh on a refresh_token that the
        // backend has already rotated and invalidated.
        const current = extractAuthentication();
        if (current.accessToken && current.accessToken !== accessToken) {
          release();
          return await api(input, options, { ...(metadata || {}), retry: true });
        }

        if (!current.refreshToken) {
          release();
          handleLogout();
          return response;
        }

        const oauth = await fetch(mBaseURL + "/api/auth/token/refresh", {
          method: "POST",
          body: JSON.stringify({ refresh_token: current.refreshToken }),
          headers: {
            "Content-Type": "application/json",
          },
        });

        const json = await oauth.json();
        const parsed = APIResponseSuccessSchema.extend({ data: AuthenticationSchema }).safeParse(json);

        if (!oauth.ok || !parsed.success) {
          // Refresh endpoint itself rejected us — session is dead.
          release();
          handleLogout();
        } else {
          persistAuthentication(parsed.data.data);
          release();
          response = await api(input, options, { ...(metadata || {}), retry: true });
        }
      } catch {
        // Network error reaching the refresh endpoint — treat as dead
        // session. (Could be debated; safer to require user to re-auth.)
        release();
        handleLogout();
      }
    } else {
      // Another refresh is in flight — wait, then retry. The recursive
      // call re-reads the (now refreshed) access token from localStorage
      // at the top of `api()`.
      await mutex.waitForUnlock();
      response = await api(input, options, { ...(metadata || {}), retry: true });
    }
  }

  return response;
}
